import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Drawer, FormControlLabel, IconButton,
  InputAdornment, Switch, TextField, Typography, Autocomplete,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ACCENT = '#427898';

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function addHour(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const total  = Math.min(h * 60 + m + 60, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function toTimeInputValue(timeStr) {
  // Normalize "HH:mm:ss" → "HH:mm" for <input type="time">
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

export default function TTEntryDrawer({
  open, onClose, onSave,
  initialEntry,    // null = new, object = edit
  initialDate,     // Date — pre-fill when clicking a day cell
  initialTime,     // "HH:mm" — pre-fill when clicking a time slot
  projects,
  users,
  isAdmin,
}) {
  const [project,   setProject]   = useState(null);
  const [date,      setDate]      = useState(new Date());
  const [startTime, setStartTime] = useState('');
  const [endTime,   setEndTime]   = useState('');
  const [hours,     setHours]     = useState('');
  const [desc,      setDesc]      = useState('');
  const [billable,      setBillable]      = useState(true);
  const [locationType,  setLocationType]  = useState('OFFICE');
  const [onBehalf,      setOnBehalf]      = useState(null);
  const [errors,        setErrors]        = useState({});

  const isEdit = Boolean(initialEntry?.id);

  // ── Auto-compute hours from start/end time ───────────────
  const computedHours = (() => {
    const sm = timeToMinutes(startTime);
    const em = timeToMinutes(endTime);
    if (sm !== null && em !== null && em > sm) return (em - sm) / 60;
    return null;
  })();

  // ── Reset form on open ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (initialEntry) {
      setProject(projects.find(p => p.id === initialEntry.projectId) || null);
      const ed = initialEntry.entryDate;
      setDate(ed ? new Date(ed + 'T00:00:00') : new Date());
      setStartTime(toTimeInputValue(initialEntry.startTime || ''));
      setEndTime(toTimeInputValue(initialEntry.endTime || ''));
      setHours(initialEntry.workingHours != null ? String(initialEntry.workingHours) : '');
      setDesc(initialEntry.description || '');
      setBillable(initialEntry.billable !== false);
      setLocationType(initialEntry.locationType || 'OFFICE');
      setOnBehalf(users.find(u => u.id === initialEntry.userId) || null);
    } else {
      setProject(null);
      setDate(initialDate || new Date());
      const st = toTimeInputValue(initialTime || '');
      setStartTime(st);
      setEndTime(st ? addHour(st) : '');
      setHours('');
      setDesc('');
      setBillable(true);
      setLocationType('OFFICE');
      setOnBehalf(null);
    }
    setErrors({});
  }, [open, initialEntry, initialDate, initialTime]);

  // ── Validation ───────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!project) e.project = 'Required';
    const hasTime  = startTime && endTime && computedHours != null && computedHours > 0;
    const hasHours = hours && !isNaN(Number(hours)) && Number(hours) > 0;
    if (!hasTime && !hasHours) e.hours = 'Enter hours or a valid start/end time';
    return e;
  };

  // ── Save ─────────────────────────────────────────────────
  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const pad = (n) => String(n).padStart(2, '0');
    const d   = date || new Date();
    const entryDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const finalHours = computedHours != null ? computedHours : Number(hours);

    onSave({
      ...(isEdit ? { id: initialEntry.id } : {}),
      project: project.id,
      workingHours: finalHours,
      description:  desc,
      entryDate,
      billable,
      locationType,
      startTime: startTime || null,
      endTime:   endTime   || null,
      ...(isAdmin && onBehalf ? { userId: onBehalf.id } : {}),
    });
  };

  const hasTimeDuration = computedHours != null && computedHours > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 440 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
      <Box sx={{ height: 3, bgcolor: ACCENT, flexShrink: 0 }} />

      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography fontWeight={700} fontSize={16} color="#0F172A">
            {isEdit ? 'Edit Entry' : 'Log Time'}
          </Typography>
          <Typography fontSize={12} color="#94A3B8" mt={0.3}>
            {isEdit ? 'Update this time entry' : 'Record time against a project'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Project */}
        <Autocomplete
          options={projects}
          getOptionLabel={p => p.name}
          value={project}
          onChange={(_, v) => { setProject(v); setErrors(e => ({ ...e, project: undefined })); }}
          renderInput={params => (
            <TextField {...params} label="Project" size="small" fullWidth
              error={!!errors.project} helperText={errors.project || ' '} />
          )}
        />

        {/* Date */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Date</Typography>
          <DatePicker
            selected={date}
            onChange={setDate}
            dateFormat="dd.MM.yyyy"
            customInput={
              <TextField size="small" fullWidth
                inputProps={{ style: { fontSize: 13 } }}
                sx={{ '& .MuiInputBase-input': { cursor: 'pointer' } }}
              />
            }
          />
        </Box>

        {/* Start + End time */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
            Time range
            <Typography component="span" sx={{ fontSize: 10, color: '#CBD5E1', fontWeight: 400, ml: 1, textTransform: 'none', letterSpacing: 0 }}>
              optional
            </Typography>
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              label="Start" size="small" type="time"
              value={startTime}
              onChange={e => { setStartTime(e.target.value); setErrors(er => ({ ...er, hours: undefined })); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
            />
            <TextField
              label="End" size="small" type="time"
              value={endTime}
              onChange={e => { setEndTime(e.target.value); setErrors(er => ({ ...er, hours: undefined })); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
            />
          </Box>

          {/* Duration readout */}
          {hasTimeDuration && (
            <Box sx={{ mt: 1, px: 1.25, py: 0.75, bgcolor: '#F0F9FF', border: '1px solid #BFDBFE', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: ACCENT }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: ACCENT }}>
                {computedHours % 1 === 0 ? computedHours : computedHours.toFixed(2)}h duration
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#64748B', ml: 0.5 }}>— hours will be set automatically</Typography>
            </Box>
          )}
        </Box>

        {/* Manual hours (shown when no valid time range) */}
        {!hasTimeDuration && (
          <TextField
            label="Hours" size="small" fullWidth
            type="number" inputProps={{ min: 0.25, step: 0.25 }}
            value={hours}
            onChange={e => { setHours(e.target.value); setErrors(er => ({ ...er, hours: undefined })); }}
            error={!!errors.hours} helperText={errors.hours || ' '}
            InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 13, color: '#94A3B8' }}>h</Typography></InputAdornment> }}
          />
        )}

        {/* Description */}
        <TextField
          label="Description" size="small" fullWidth multiline rows={3}
          placeholder="What did you work on?"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          helperText=" "
          inputProps={{ style: { fontSize: 13 } }}
        />

        {/* Location type */}
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>Location</Typography>
          <ToggleButtonGroup
            value={locationType} exclusive size="small" fullWidth
            onChange={(_, v) => { if (v) setLocationType(v); }}
          >
            <ToggleButton value="OFFICE"     sx={{ flex: 1, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>🏢 Office</ToggleButton>
            <ToggleButton value="REMOTE"     sx={{ flex: 1, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>🏠 Remote</ToggleButton>
            <ToggleButton value="CLIENT_SITE" sx={{ flex: 1, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>📍 Client</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Billable toggle */}
        <Box sx={{ p: 1.5, bgcolor: billable ? '#F0FDF4' : '#F8FAFC', borderRadius: 1.5, border: `1px solid ${billable ? '#BBF7D0' : '#E2E8F0'}`, transition: 'all 0.2s' }}>
          <FormControlLabel
            control={
              <Switch checked={billable} onChange={e => setBillable(e.target.checked)} size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#16A34A' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16A34A' } }} />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: billable ? '#15803D' : '#64748B' }}>
                  {billable ? 'Billable' : 'Internal / Non-billable'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                  {billable ? 'Will appear in invoice generation' : 'Excluded from invoices'}
                </Typography>
              </Box>
            }
          />
          {billable && (
            <Chip size="small" icon={<AttachMoneyIcon style={{ fontSize: 12 }} />} label="Client charged"
              sx={{ bgcolor: '#DCFCE7', color: '#15803D', fontSize: 10, height: 18, mt: 0.5, ml: 0.5, '& .MuiChip-icon': { color: '#15803D' } }} />
          )}
        </Box>

        {/* On behalf of (admin only) */}
        {isAdmin && users.length > 0 && (
          <Autocomplete
            options={users}
            getOptionLabel={u => `${u.firstName} ${u.lastName}`}
            value={onBehalf}
            onChange={(_, v) => setOnBehalf(v)}
            renderInput={params => (
              <TextField {...params} label="Log on behalf of" size="small" fullWidth
                helperText={onBehalf ? `Entry attributed to ${onBehalf.firstName}` : 'Leave empty to log as yourself'} />
            )}
          />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}
          sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
          {isEdit ? 'Save changes' : 'Log time'}
        </Button>
      </Box>
    </Drawer>
  );
}
